import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, Save, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { assetUrl, contrastWithWhite, DEFAULT_SITE } from "@/lib/site";
import { PageTitle, Panel } from "@/components/admin/AdminLayout";

const input = "w-full h-10 px-3 rounded-lg border border-slate-300 text-sm focus:border-emerald-500 outline-none";
const Field = ({ label, help, children }) => (
  <label className="block space-y-1.5">
    <span className="text-sm font-semibold">{label}</span>
    {children}
    {help && <span className="block text-xs text-slate-500">{help}</span>}
  </label>
);

const ImageField = ({ label, help, value, onChange, testid, previewClass = "h-12" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 2_000_000) return toast.error("Image must be under 2 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/branding/upload", fd);
      onChange(data.url);
      toast.success("Uploaded. Click Save to apply it to the site.");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Field label={label} help={help}>
      <div className="flex gap-2">
        <input className={input} value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload a file" data-testid={`${testid}-input`} />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-upload-button`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
          <Upload size={15} /> {busy ? "Uploading…" : "Upload"}
        </button>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,image/gif,.ico" className="hidden" onChange={upload} />
      </div>
      {value && (
        <span className="flex items-center gap-3 mt-2">
          <img src={assetUrl(value)} alt="" className={`${previewClass} max-w-[240px] w-auto object-contain rounded border border-slate-200 bg-slate-50 p-1`} data-testid={`${testid}-preview`} />
          <button type="button" onClick={() => onChange("")} className="text-xs text-red-600 font-semibold" data-testid={`${testid}-remove`}>Remove</button>
        </span>
      )}
    </Field>
  );
};

const ColourField = ({ label, help, value, onChange, fallback, testid }) => (
  <Field label={label} help={help}>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded border border-slate-300 cursor-pointer" data-testid={`${testid}-picker`} />
      <input className={`${input} w-32 font-mono`} value={value} onChange={(e) => onChange(e.target.value)} maxLength={7} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => onChange(fallback)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid={`${testid}-reset`}><RotateCcw size={13} /> Default</button>
    </div>
  </Field>
);

const Count = ({ v, max }) => <span className={v.length > max ? "text-red-600" : ""}>{v.length}/{max} characters</span>;

export default function BrandingPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data && !f) setF({ ...DEFAULT_SITE, ...data.site }); }, [data, f]);
  if (!f) return <p className="text-slate-500">Loading…</p>;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const lowContrast = /^#[0-9a-f]{6}$/i.test(f.primary_color) && contrastWithWhite(f.primary_color) < 3;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", { site: f });
      setF({ ...DEFAULT_SITE, ...r.data.site });
      qc.setQueryData(["settings"], r.data);
      qc.invalidateQueries({ queryKey: ["public-site"] });
      toast.success("Site branding saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6 max-w-4xl" data-testid="branding-page">
      <PageTitle title="Site branding" sub="Logo, favicon, site name, colours and SEO defaults for the public website.">
        <button onClick={save} disabled={saving} data-testid="branding-save-button"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-full disabled:opacity-50 transition-colors">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </button>
      </PageTitle>
      <Panel title="Logo & favicon">
        <div className="grid gap-6 md:grid-cols-2">
          <ImageField label="Site logo" help="Resized automatically to fit the header, footer and admin menu (any shape works; wide PNG/SVG with a transparent background looks best). Leave empty to use the name with the ₹ badge." value={f.logo_url} onChange={set("logo_url")} testid="branding-logo" />
          <ImageField label="Light logo (dark backgrounds)" help="A white or light version for the dark footer and admin menu. Leave empty to use the site logo there." value={f.logo_light_url} onChange={set("logo_light_url")} testid="branding-logo-light" previewClass="h-12 bg-slate-900 p-1" />
          <ImageField label="Favicon" help="The small browser-tab icon. Square PNG, SVG or ICO, 64×64 px or larger." value={f.favicon_url} onChange={set("favicon_url")} testid="branding-favicon" previewClass="h-10 w-10" />
        </div>
      </Panel>
      <Panel title="Basic details">
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Site name" help="Used in the header (when no logo), page titles and the footer."><input className={input} value={f.name} onChange={set("name")} maxLength={40} data-testid="branding-name-input" /></Field>
          <Field label="Tagline"><input className={input} value={f.tagline} onChange={set("tagline")} maxLength={80} data-testid="branding-tagline-input" /></Field>
          <Field label="Contact email" help="Shown on the contact page."><input className={input} type="email" value={f.contact_email} onChange={set("contact_email")} data-testid="branding-email-input" /></Field>
        </div>
      </Panel>
      <Panel title="Colours">
        <div className="grid gap-6 md:grid-cols-2">
          <ColourField label="Main colour" help="Buttons, links, active menu items and highlights." value={f.primary_color} onChange={set("primary_color")} fallback={DEFAULT_SITE.primary_color} testid="branding-primary" />
          <ColourField label="Accent colour" help="Badges, the ₹ badge and small highlights." value={f.accent_color} onChange={set("accent_color")} fallback={DEFAULT_SITE.accent_color} testid="branding-accent" />
        </div>
        {lowContrast && (
          <p className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-4" data-testid="branding-contrast-warning">
            <AlertTriangle size={16} /> This main colour is light, so white text on buttons may be hard to read. Pick a darker shade.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-5" data-testid="branding-colour-preview">
          <span className="px-4 py-2 rounded-full text-white text-sm font-bold" style={{ background: f.primary_color }}>Get deal</span>
          <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: f.accent_color }}>62% OFF</span>
          <span className="text-sm font-semibold" style={{ color: f.primary_color }}>View all deals →</span>
        </div>
      </Panel>
      <Panel title="SEO & sharing">
        <div className="grid gap-6">
          <Field label="Default page title" help={<><Count v={f.seo_title} max={70} /> · Used on the home page and any page without its own title.</>}>
            <input className={input} value={f.seo_title} onChange={set("seo_title")} data-testid="branding-seo-title-input" />
          </Field>
          <Field label="Default description" help={<><Count v={f.seo_description} max={170} /> · Shown under your link in Google results.</>}>
            <textarea className={`${input} h-20 py-2`} value={f.seo_description} onChange={set("seo_description")} data-testid="branding-seo-description-input" />
          </Field>
          <ImageField label="Share image" help="Shown when a page link is shared and the page has no image of its own. 1200×630 px works best." value={f.og_image_url} onChange={set("og_image_url")} testid="branding-og-image" previewClass="h-20" />
          <div className="grid gap-6 md:grid-cols-2">
            <Field label="Google Analytics ID" help="Looks like G-XXXXXXXXXX. Loads on public pages only, not the admin panel.">
              <input className={`${input} font-mono`} value={f.ga_id} onChange={set("ga_id")} placeholder="G-XXXXXXXXXX" data-testid="branding-ga-input" />
            </Field>
            <Field label="Google Search Console code" help="Paste the meta tag Google gives you, or just its content value.">
              <input className={`${input} font-mono`} value={f.gsc_verification} onChange={set("gsc_verification")} placeholder='<meta name="google-site-verification" content="…">' data-testid="branding-gsc-input" />
            </Field>
          </div>
        </div>
      </Panel>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="branding-save-bottom-button"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-full disabled:opacity-50 transition-colors">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
